import { useMemo } from 'react';

import { useGpuPrerequisites } from './useGpuPrerequisites';
import { useWorkloadsGpuUtilizationSummaryQuery } from '@cast/split-me/hooks';

import {
  aggregateGpuSummary,
  GpuSummaryAggregate,
} from '../_utils/aggregateGpuSummary';

import { useGpuInventoryDateRange } from './useGpuInventoryDateRange';

const PAGE_LIMIT = 1000;

export type UseGpuSummaryDataResult = {
  data: GpuSummaryAggregate | undefined;
  isLoading: boolean;
  isCapabilitiesLoading: boolean;
  hasGpuMetrics: boolean;
  hasGpuData: boolean;
  isDataProcessing: boolean;
  isGpuExporterInstalled: boolean;
  error: unknown;
  refetch: () => void;
};

export const useGpuSummaryData = (): UseGpuSummaryDataResult => {
  const { adjustedDateRange } = useGpuInventoryDateRange();
  const {
    clusterId,
    hasGpuMetrics,
    hasGpuData,
    isDataProcessing,
    isGpuExporterInstalled,
    isCapabilitiesLoading,
    error: prerequisitesError,
    refetch: refetchPrerequisites,
  } = useGpuPrerequisites({
    startTime: adjustedDateRange[0].toISOString(),
    endTime: adjustedDateRange[1].toISOString(),
  });

  const [startTime, endTime] = [
    adjustedDateRange[0].toISOString(),
    adjustedDateRange[1].toISOString(),
  ];

  const {
    data: workloads,
    isLoading: isQueryLoading,
    count,
    error: queryError,
    refetch: refetchQuery,
  } = useWorkloadsGpuUtilizationSummaryQuery({
    clusterId,
    startTime,
    endTime,
    pageLimit: PAGE_LIMIT,
    enabled: !!clusterId && hasGpuMetrics,
  });

  const data = useMemo(
    () => aggregateGpuSummary(workloads, count),
    [workloads, count]
  );

  return {
    data: isCapabilitiesLoading || isQueryLoading ? undefined : data,
    isLoading: isCapabilitiesLoading || isQueryLoading,
    isCapabilitiesLoading,
    hasGpuMetrics,
    hasGpuData,
    isDataProcessing,
    isGpuExporterInstalled,
    error: prerequisitesError || queryError,
    refetch: () => {
      refetchPrerequisites();
      refetchQuery?.();
    },
  };
};
